import {
  TextField,
  Checkbox,
  FormControlLabel,
  Box,
  Typography,
  Paper,
  Grid,
  Radio,
  RadioGroup,
  Button,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { LocalizationProvider, DatePicker, TimePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { Clock } from "lucide-react";
import React from "react";
import { useTranslation } from "react-i18next";

import { ScheduleState } from "../Logic/BusinessSchedule";

type TimePeriod = "morningOpen" | "morningClose" | "afternoonOpen" | "afternoonClose";

interface BusinessScheduleUIProps {
  addEnableButton: boolean;
  schedule: ScheduleState;
  vacationStart: Date | null;
  vacationEnd: Date | null;
  handleDayToggle: (day: string) => void;
  handleContinuousToggle: (day: string) => void;
  handleTimeChange: (day: string, period: TimePeriod, value: string) => void;
  handlePeriodChange: (day: string, value: "both" | "morning" | "afternoon") => void;
  handleDateChange: (type: "start" | "end", value: Date | null) => void;
  submit: () => Promise<void>;
  parseISO: (isoString: string) => Date;
  format: (date: Date, formatString: string) => string;
}

const BusinessScheduleUI = ({
  addEnableButton,
  schedule,
  vacationStart,
  vacationEnd,
  handleDayToggle,
  handleContinuousToggle,
  handleTimeChange,
  handlePeriodChange,
  handleDateChange,
  submit,
  parseISO,
  format,
}: BusinessScheduleUIProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      {addEnableButton && (
        <Button
          onClick={submit}
          sx={{
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            padding: "10px 20px",
            borderRadius: theme.shape.borderRadius,
            transition: "background-color 0.3s ease",
            position: "absolute",
            right: "60px",
            top: "45px",

            "&:hover": {
              backgroundColor: theme.palette.primary.dark,
              color: theme.palette.primary.contrastText,
            },
          }}
        >
          Submit
        </Button>
      )}

      <Paper
        elevation={3}
        sx={{ maxWidth: "100%", mx: "auto", p: 4, borderRadius: 2, boxShadow: "unset" }}
      >
        <Typography
          variant="h5"
          display="flex"
          alignItems="center"
          gap={1}
          fontWeight="medium"
          mb={3}
        >
          <Clock size={24} />
          Orari Negozio
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {Object.entries(schedule).map(([day, daySchedule]) => (
            <Paper
              key={day}
              sx={{
                p: 2,
                borderRadius: 4,
                boxShadow: "unset",
                border: "1px solid #dddddd",
                bgcolor: daySchedule.isOpen ? "white" : "grey.200",
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox checked={daySchedule.isOpen} onChange={() => handleDayToggle(day)} />
                }
                label={
                  <Typography variant="body1" sx={{ textTransform: "capitalize" }}>
                    {t(`days.${day}`)}
                  </Typography>
                }
                sx={{
                  display: "flex",
                  paddingLeft: "25px",
                }}
              />

              <Box sx={{ ml: 3 }}>
                {daySchedule.isOpen && (
                  <RadioGroup
                    row
                    value={daySchedule.openPeriod}
                    onChange={(e) =>
                      handlePeriodChange(day, e.target.value as "both" | "morning" | "afternoon")
                    }
                    sx={{
                      paddingBottom: "10px",
                    }}
                  >
                    <FormControlLabel
                      sx={{
                        "& .MuiFormControlLabel-label": {
                          fontWeight: 400,
                          marginRight: "15px",
                        },
                      }}
                      value="both"
                      control={<Radio />}
                      label="Mattina e Pomeriggio"
                    />
                    <FormControlLabel
                      sx={{
                        "& .MuiFormControlLabel-label": {
                          fontWeight: 400,
                          marginRight: "15px",
                        },
                      }}
                      value="morning"
                      control={<Radio />}
                      label="Solo Mattina"
                    />
                    <FormControlLabel
                      sx={{
                        "& .MuiFormControlLabel-label": {
                          fontWeight: 400,
                          marginRight: "15px",
                        },
                      }}
                      value="afternoon"
                      control={<Radio />}
                      label="Solo Pomeriggio"
                    />
                  </RadioGroup>
                )}

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={daySchedule.isContinuous}
                      onChange={() => handleContinuousToggle(day)}
                      disabled={!daySchedule.isOpen || daySchedule.openPeriod !== "both"}
                    />
                  }
                  label={<Typography variant="body2">Orario continuato</Typography>}
                  sx={{
                    display: "flex",
                    borderTop: "0.8px solid #ddd",
                    paddingTop: "10px",
                    paddingBottom: "10px",
                  }}
                />

                <Grid container spacing={2}>
                  <Grid
                    item
                    xs={6}
                    sx={{ display: daySchedule.openPeriod === "afternoon" ? "none" : "block" }}
                  >
                    <Typography variant="body2" fontWeight="medium" mb={1}>
                      Mattina
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                      <TimePicker
                        value={parseISO(`2024-01-01T${daySchedule.morningOpen}`)}
                        onChange={(newValue) => {
                          if (newValue) {
                            handleTimeChange(day, "morningOpen", format(newValue, "HH:mm"));
                          }
                        }}
                        disabled={!daySchedule.isOpen}
                        slotProps={{
                          textField: {
                            size: "small",
                            fullWidth: true,
                            className: "morningOpen",
                            sx: {
                              "& .MuiInputBase-root": {
                                justifyContent: "space-between",
                              },
                              "& input": {
                                textAlign: "left",
                              },
                            },
                          },
                        }}
                        views={["hours", "minutes"]}
                        ampm={false}
                        minutesStep={5}
                        minTime={parseISO("2024-01-01T00:00")}
                        maxTime={parseISO("2024-01-01T12:59")}
                      />
                      <Typography>-</Typography>
                      <TimePicker
                        value={parseISO(`2024-01-01T${daySchedule.morningClose}`)}
                        onChange={(newValue) => {
                          if (newValue) {
                            handleTimeChange(day, "morningClose", format(newValue, "HH:mm"));
                          }
                        }}
                        disabled={!daySchedule.isOpen || daySchedule.isContinuous}
                        slotProps={{
                          textField: {
                            size: "small",
                            fullWidth: true,
                            className: "morningClose",
                            sx: {
                              "& .MuiInputBase-root": {
                                justifyContent: "space-between",
                              },
                              "& input": {
                                textAlign: "left",
                              },
                            },
                          },
                        }}
                        views={["hours", "minutes"]}
                        ampm={false}
                        minutesStep={5}
                        minTime={parseISO(`2024-01-01T${daySchedule.morningOpen}`)}
                        maxTime={parseISO("2024-01-01T12:59")}
                      />
                    </Box>
                  </Grid>

                  <Grid
                    item
                    xs={6}
                    sx={{ display: daySchedule.openPeriod === "morning" ? "none" : "block" }}
                  >
                    <Typography variant="body2" fontWeight="medium" mb={1}>
                      Pomeriggio
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                      <TimePicker
                        value={parseISO(`2024-01-01T${daySchedule.afternoonOpen}`)}
                        onChange={(newValue) => {
                          if (newValue) {
                            handleTimeChange(day, "afternoonOpen", format(newValue, "HH:mm"));
                          }
                        }}
                        disabled={!daySchedule.isOpen || daySchedule.isContinuous}
                        slotProps={{
                          textField: {
                            size: "small",
                            fullWidth: true,
                            className: "afternoonOpen",
                            sx: {
                              "& .MuiInputBase-root": {
                                justifyContent: "space-between",
                              },
                              "& input": {
                                textAlign: "left",
                              },
                            },
                          },
                        }}
                        views={["hours", "minutes"]}
                        ampm={false}
                        minutesStep={5}
                        minTime={parseISO("2024-01-01T13:00")}
                        maxTime={parseISO("2024-01-01T23:59")}
                      />
                      <Typography>-</Typography>
                      <TimePicker
                        value={parseISO(`2024-01-01T${daySchedule.afternoonClose}`)}
                        onChange={(newValue) => {
                          if (newValue) {
                            handleTimeChange(day, "afternoonClose", format(newValue, "HH:mm"));
                          }
                        }}
                        disabled={!daySchedule.isOpen}
                        slotProps={{
                          textField: {
                            size: "small",
                            fullWidth: true,
                            className: "afternoonClose",
                            sx: {
                              "& .MuiInputBase-root": {
                                justifyContent: "space-between",
                              },
                              "& input": {
                                textAlign: "left",
                              },
                            },
                          },
                        }}
                        views={["hours", "minutes"]}
                        ampm={false}
                        minutesStep={5}
                        minTime={parseISO(`2024-01-01T${daySchedule.afternoonOpen}`)}
                        maxTime={parseISO("2024-01-01T23:59")}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          ))}
        </Box>

        <Paper
          sx={{ mt: 4, p: 3, borderRadius: 4, boxShadow: "unset", border: "1px solid #dddddd" }}
        >
          <Typography variant="h5" fontWeight="bold" mb={2}>
            Periodo di Ferie
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="body2" mb={1}>
                Data inizio
              </Typography>
              <DatePicker
                value={vacationStart}
                onChange={(newValue) => handleDateChange("start", newValue)}
                slots={{ textField: TextField }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                    sx: {
                      "& .MuiInputBase-root": {
                        justifyContent: "space-between",
                      },
                      "& input": {
                        textAlign: "left",
                      },
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" mb={1}>
                Data fine
              </Typography>
              <DatePicker
                value={vacationEnd}
                onChange={(newValue) => handleDateChange("end", newValue)}
                slots={{ textField: TextField }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                    sx: {
                      "& .MuiInputBase-root": {
                        justifyContent: "space-between",
                      },
                      "& input": {
                        textAlign: "left",
                      },
                    },
                  },
                }}
              />
            </Grid>
          </Grid>
        </Paper>
      </Paper>
    </LocalizationProvider>
  );
};

export default BusinessScheduleUI;
